import { useCallback, useEffect, useRef, useState } from 'react';

// Browser Web Speech API (Chrome/Safari prefix it; Firefox has none). Undefined
// elsewhere → we degrade silently and the caller hides the mic button, per the
// product non-negotiable: "Voice input must gracefully degrade … fall back to
// text input silently."
const SpeechRecognitionImpl: unknown =
  typeof window !== 'undefined'
    ? (window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown }).SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition
    : undefined;

export interface UseVoiceInput {
  /** False when the browser has no SpeechRecognition — hide the mic button. */
  supported: boolean;
  recording: boolean;
  /** Start dictation (or stop it if already recording). */
  toggle: () => void;
  stop: () => void;
}

/**
 * Thin wrapper over the Web Speech API for one-shot dictation. On a final result
 * it calls `onTranscript(text)` (the caller appends it to its input). No-ops when
 * unsupported. The callback is read through a ref so a re-render doesn't restart
 * an in-flight recognition session.
 */
export function useVoiceInput(onTranscript: (text: string) => void): UseVoiceInput {
  const [recording, setRecording] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);
  const cbRef = useRef(onTranscript);
  cbRef.current = onTranscript;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setRecording(false);
  }, []);

  const toggle = useCallback(() => {
    if (!SpeechRecognitionImpl) return;
    if (recognitionRef.current && recording) {
      recognitionRef.current.stop();
      setRecording(false);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recognition = new (SpeechRecognitionImpl as any)();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      const transcript = event.results?.[0]?.[0]?.transcript ?? '';
      if (transcript) cbRef.current(transcript);
    };
    recognition.onerror = () => setRecording(false);
    recognition.onend = () => setRecording(false);
    recognition.start();
    recognitionRef.current = recognition;
    setRecording(true);
  }, [recording]);

  // Stop dictation if the component unmounts mid-recording.
  useEffect(() => () => recognitionRef.current?.stop(), []);

  return { supported: !!SpeechRecognitionImpl, recording, toggle, stop };
}
